import { useBunja } from 'bunja/react';
import { intervalToDuration } from 'date-fns';
import { useAtomValue, useSetAtom } from 'jotai';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useInterval } from 'react-use';

import { OrderSideBadge } from '@flipster/features/src/misc/badge';
import { SymbolDisplay } from '@flipster/features/src/misc/display';
import { OrderAttributionMapper, orderAttributionAtom } from '@flipster/features/src/shared/order-attribution';
import { tradingLeaderboardMyRankingBunja } from '@flipster/features/src/trading-leaderboard-page/bunja';
import { FormattedMessage } from '@flipster/i18n';
import { Carousel } from '@flipster/react/design-system-legacy/carousel';
import { TextButton } from '@flipster/react/design-system/text-button';
import Formatted from '@flipster/react/design-utils/formatted';
import CrownIcon from '@flipster/react/icon/generated/CrownIcon';
import UserCircleIcon from '@flipster/react/icon/generated/UserCircleIcon';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { on } from '@flipster/utils/dom-event-listener';
import segment from '@flipster/utils/segment';
import { MarketEvent } from '@flipster/utils/segment/events/market';

import { TopTraderOpenPositionWithSymbol, topTraderOpenPositionsBunja } from './bunja';

export const TopTraderCarousel = () => {
  const { myIntervalRankingsAtom } = useBunja(tradingLeaderboardMyRankingBunja);
  const myIntervalRankings = useAtomValue(myIntervalRankingsAtom);

  const { openPositionsAtom, temporalFeatureFlagAtom } = useBunja(topTraderOpenPositionsBunja);
  const openPositions = useAtomValue(openPositionsAtom);
  const temporalFeatureFlag = useAtomValue(temporalFeatureFlagAtom);

  // TODO: remove this after some time passed since the deployment
  if (temporalFeatureFlag) return null;
  return (
    <Carousel
      slidesPerView='auto'
      spaceBetween={8}
      title={
        <h2 className={css({ textStyle: 'Title/L', color: 'content.default.level-5', mb: 16 })}>
          <FormattedMessage id='Market.r250919.Top_traders_positions' />
        </h2>
      }
      navigator={
        <>
          <TextButton
            as='a'
            href={`/trading-leaderboard?tier=${myIntervalRankings.WEEKLY?.tier ?? 'ALL'}&interval=WEEKLY`}
            label={<FormattedMessage id='Common.r250919.See_ranks' />}
            color='neutral'
            css={{ gridArea: 'arrow-right', tabletDown: { pr: 16 } }}
            onClick={() => segment.track(MarketEvent.TopTradersPositionsSeeRanksClick)}
          />
          <Carousel.Navigator
            css={{ gridArea: 'arrow-bottom', tablet: { display: 'none' } }}
            onPrev={() => segment.track(MarketEvent.TopTradersPositionsArrowClick)}
            onNext={() => segment.track(MarketEvent.TopTradersPositionsArrowClick)}
          />
        </>
      }
      className={css({ pl: { base: 16, tablet: 0 }, pb: { base: 32, tablet: 48 } })}
    >
      {(() => {
        if (openPositions.length == 0) {
          return (
            <Carousel.Slide css={{ w: '100%' }}>
              <TraderPositionUpdateBanner />
            </Carousel.Slide>
          );
        }
        return openPositions.map(({ position, openedAt, pricePrecision }) => (
          <Carousel.Slide css={{ w: 310, tabletDown: { w: 'calc(100vw - 32px)' } }} key={position.id}>
            <TraderPositionBanner position={position} openedAt={openedAt} pricePrecision={pricePrecision} />
          </Carousel.Slide>
        ));
      })()}
    </Carousel>
  );
};

const TraderPositionBanner = ({
  position: { symbol, userProfileId, nickname, weeklyLeaderboardRank, entryPrice, side },
  pricePrecision,
  openedAt,
}: TopTraderOpenPositionWithSymbol) => {
  const { landingTestAtom } = useBunja(topTraderOpenPositionsBunja);
  const landingTest = useAtomValue(landingTestAtom);
  const [now, setNow] = useState(new Date());
  const setOrderAttribution = useSetAtom(orderAttributionAtom);

  useInterval(() => setNow(new Date()), 60 * 1000);
  useEffect(() => on(document, 'visibilitychange', () => setNow(new Date())), []);
  const openedMinutes = (() => {
    const { minutes } = intervalToDuration({ start: openedAt, end: now });
    return (minutes ?? 0) + 1;
  })();
  return (
    <Link
      href={
        landingTest === 'tradingActivity' ? `/user/profile/${userProfileId}?tab=activity` : `/trade/perpetual/${symbol}`
      }
      onClick={() => {
        setOrderAttribution(OrderAttributionMapper.market.topTradersOpenPositions(userProfileId));
        segment.track(MarketEvent.TopTradersPositionsClick, {
          symbol,
          profile_id: userProfileId,
          profile_ranking: weeklyLeaderboardRank.toString(),
          profile_username: nickname,
          position: side === 'Long' ? 'buy' : 'sell',
          entry_price: Number(entryPrice),
          position_open_duration: `${openedMinutes}m`,
          event_location: 'market_tab',
        });
      }}
    >
      <div
        className={stack({
          padding: 16,
          gap: 8,
          borderRadius: 8,
          cursor: 'pointer',
          backgroundColor: 'background.default.level-1',
          _hover: {
            backgroundColor: 'background.default.level-3',
          },
        })}
      >
        <div className={hstack({ gap: 8 })}>
          <SymbolDisplay
            symbol={symbol}
            icon={<SymbolDisplay.Icon size={24} space={4} />}
            mainLabel={
              <SymbolDisplay.Label
                value='shortName'
                css={{ textStyle: 'Text/L/Strong', color: 'content.default.level-5' }}
              />
            }
          />
          <OrderSideBadge orderSide={side} />
        </div>
        <div className={stack({ gap: 4, color: 'content.default.level-2', textStyle: 'Text/M/Regular' })}>
          <div className={hstack({ gap: 4 })}>
            <div className={hstack({ gap: 2, minWidth: 0 })}>
              <UserCircleIcon size={16} color={token('colors.content.default.level-2')} />
              <span className={css({ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' })}>
                {nickname}
              </span>
            </div>
            <span>•</span>
            <div className={css({ flexShrink: 0 })}>
              <span>
                <FormattedMessage id='Market.r250919.Entry_colon' />{' '}
              </span>
              <Formatted.Number
                method='round-half-up'
                value={entryPrice}
                precision={pricePrecision}
                toFixed
                css={{ color: 'content.default.level-5' }}
              />
            </div>
          </div>
          <div className={hstack({ gap: 4 })}>
            <div className={hstack({ gap: 2 })}>
              <CrownIcon size={16} color={token('colors.content.default.level-2')} />
              P&L <FormattedMessage id='Common.r251015.rank_colon_rank' values={{ rank: weeklyLeaderboardRank }} />
            </div>
            <span>•</span>
            <div>
              <FormattedMessage id='Common.r250919.Open_minute_m_ago' values={{ minute: openedMinutes }} />
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

const TraderPositionUpdateBanner = () => {
  const { myIntervalRankingsAtom } = useBunja(tradingLeaderboardMyRankingBunja);
  const myIntervalRankings = useAtomValue(myIntervalRankingsAtom);
  return (
    <div
      className={flex({
        borderRadius: 8,
        backgroundColor: 'background.default.level-1',
        tabletDown: {
          padding: 16,
          flexDirection: 'column-reverse',
          gap: 4,
        },
        tablet: {
          padding: '30px 16px',
          justifyContent: 'space-between',
        },
        maxW: 'calc(100vw - 32px)',
      })}
    >
      <div className={flex({ gap: 8, tablet: { flexDirection: 'column' } })}>
        <p>
          <span className={css({ tabletDown: { display: 'none' } })}>
            <FormattedMessage id='Market.r251015.We_are_updating_top_trader_positions' />
          </span>
          <span className={css({ tablet: { display: 'none' } })}>
            <FormattedMessage id='Market.r251015.Updating_top_trader_positions' />
          </span>
        </p>
        <Link href={`/trading-leaderboard?tier=${myIntervalRankings.WEEKLY?.tier ?? 'ALL'}&interval=WEEKLY`}>
          <TextButton
            label={<FormattedMessage id='Common.r250401.Learn_more' />}
            onClick={() => segment.track(MarketEvent.TopTradersPositionsLearnMoreClick)}
          />
        </Link>
      </div>
      <div className={hstack()}>
        {SamplePositions.map((position) => {
          return (
            <div
              key={position.nickname}
              className={stack({
                gap: 8,
                width: 270,
                filter: 'blur(4px)',
                display: 'none',
                _first: { display: 'inherit' },
                tablet: { '&:nth-child(2)': { display: 'inherit' } },
                smallDesktop: { '&:nth-child(3)': { display: 'inherit' } },
              })}
            >
              <div className={hstack({ gap: 8 })}>
                <SymbolDisplay
                  symbol={position.symbol}
                  icon={<SymbolDisplay.Icon size={24} space={4} />}
                  mainLabel={
                    <SymbolDisplay.Label
                      value='shortName'
                      css={{ textStyle: 'Text/L/Strong', color: 'content.default.level-5' }}
                    />
                  }
                />
                <OrderSideBadge orderSide={position.side} />
              </div>
              <div className={hstack({ gap: 2 })}>
                <UserCircleIcon size={16} color={token('colors.content.default.level-2')} />
                <span>{position.nickname}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SamplePositions = [
  {
    symbol: 'BTCUSDT.PERP',
    side: 'Short',
    nickname: 'Flipsterrules',
  },
  {
    symbol: 'ETHUSDT.PERP',
    side: 'Long',
    nickname: 'JohnDoe',
  },
  {
    symbol: 'SOLUSDT.PERP',
    side: 'Short',
    nickname: 'Go_Flipster',
  },
] as const;
